import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import {
  activateContractor,
  deactivateContractor,
  patchContractor,
  deleteContractor,
} from "@/services/contractors";
import { ReadContractor, UpdateContractor } from "@/types/contractor";
import { ApiError } from "@/services/api";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { Module } from "@/constants";
import { ContractorHandlerKey } from "../context/ContractorContext";

interface UseContractorHandlersProps {
  contractorId: string;
  openDialog?: (
    handlerKey: ContractorHandlerKey,
    onConfirm: () => void,
  ) => void;
  closeDialog?: () => void;
}

export const useContractorHandlers = ({
  contractorId,
  openDialog,
  closeDialog,
}: UseContractorHandlersProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const contractorQueryKey = [Module.Contractors, contractorId];

  const activateContractorMutation = useMutationWithErrorHandling<
    ReadContractor,
    ApiError,
    undefined,
    unknown
  >({
    mutationFn: () => activateContractor(contractorId),
    errorTitle: t("contractors.errors.activate"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [Module.Contractors] });
      queryClient.invalidateQueries({ queryKey: contractorQueryKey });
      toast({
        title: t("contractors.success.activated"),
      });
      closeDialog?.();
    },
  });

  const deactivateContractorMutation = useMutationWithErrorHandling<
    ReadContractor,
    ApiError,
    undefined,
    unknown
  >({
    mutationFn: () => deactivateContractor(contractorId),
    errorTitle: t("contractors.errors.deactivate"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [Module.Contractors] });
      queryClient.invalidateQueries({ queryKey: contractorQueryKey });
      toast({
        title: t("contractors.success.deactivated"),
      });
      closeDialog?.();
    },
  });

  const updateContractorMutation = useMutationWithErrorHandling<
    ReadContractor,
    ApiError,
    UpdateContractor,
    unknown
  >({
    mutationFn: (updateData) => patchContractor(contractorId, updateData),
    errorTitle: t("contractors.errors.update"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [Module.Contractors] });
      queryClient.invalidateQueries({ queryKey: contractorQueryKey });
      toast({
        title: t("contractors.success.updated"),
      });
    },
  });

  const deleteContractorMutation = useMutationWithErrorHandling<
    unknown,
    ApiError,
    undefined,
    unknown
  >({
    mutationFn: () => deleteContractor(contractorId),
    errorTitle: t("contractors.errors.delete"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [Module.Contractors] });
      queryClient.invalidateQueries({ queryKey: contractorQueryKey });
      toast({
        title: t("contractors.success.deleted"),
      });
    },
  });

  // Create mutations object for easier passing to internal hooks
  const mutations = {
    activateContractorMutation,
    deactivateContractorMutation,
    updateContractorMutation,
    deleteContractorMutation,
  };

  // Handler functions
  const handleActivate = useCallback(() => {
    if (openDialog) {
      openDialog("activate", () =>
        activateContractorMutation.mutate(undefined),
      );
    } else {
      activateContractorMutation.mutate(undefined);
    }
  }, [activateContractorMutation, openDialog]);

  const handleDeactivate = useCallback(() => {
    if (openDialog) {
      openDialog("deactivate", () =>
        deactivateContractorMutation.mutate(undefined),
      );
    } else {
      deactivateContractorMutation.mutate(undefined);
    }
  }, [deactivateContractorMutation, openDialog]);

  const handleUpdate = useCallback(
    (updateData: UpdateContractor) =>
      updateContractorMutation.mutate(updateData),
    [updateContractorMutation],
  );

  const handleDelete = useCallback(
    () => deleteContractorMutation.mutate(undefined),
    [deleteContractorMutation],
  );

  return {
    handleActivate,
    handleDeactivate,
    handleUpdate,
    handleDelete,

    // Mutations object for passing to other hooks
    mutations,
  };
};
